import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { 
  Award, AlertCircle, Plus, Trash2, User, Calendar, 
  TrendingUp, TrendingDown, DollarSign, CreditCard, Wallet,
  Target, Camera, Check, X, Star, Send, Image, Eye, Edit,
  Wrench, Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';
import { ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from 'recharts';
import { useAuth } from '@/context/AuthContext';
import { useSettings } from '@/context/SettingsContext';
import usePermissions from '@/hooks/usePermissions';
import PullToRefresh from '@/components/PullToRefresh';
import { DateInput } from '@/components/ui/date-input';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const PAGE_SIZE = 20;

const fmtDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const presetRange = (preset) => {
  const now = new Date();
  switch (preset) {
    case 'mtd':
      return { start: fmtDate(new Date(now.getFullYear(), now.getMonth(), 1)), end: fmtDate(now) };
    case 'last_30': {
      const s = new Date(now); s.setDate(s.getDate() - 30);
      return { start: fmtDate(s), end: fmtDate(now) };
    }
    case 'last_month':
      return {
        start: fmtDate(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
        end: fmtDate(new Date(now.getFullYear(), now.getMonth(), 0))
      };
    case 'last_90': {
      const s = new Date(now); s.setDate(s.getDate() - 90);
      return { start: fmtDate(s), end: fmtDate(now) };
    }
    case 'ytd':
      return { start: fmtDate(new Date(now.getFullYear(), 0, 1)), end: fmtDate(now) };
    default:
      return { start: '', end: '' };
  }
};

const IncentivesPage = () => {
  const { user } = useAuth();
  const { formatCurrency } = useSettings();
  const { hasPermission, isAdmin } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [incentives, setIncentives] = useState([]);
  const [totals, setTotals] = useState({ total_incentives: 0, total_penalties: 0, net_total: 0 });
  const [users, setUsers] = useState([]);
  // History filters + pagination
  const [filterUserId, setFilterUserId] = useState('all');
  const [filterRange, setFilterRange] = useState({ start: '', end: '' });
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  // Analytics
  const [analytics, setAnalytics] = useState(null);
  const [analyticsPreset, setAnalyticsPreset] = useState('last_30');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [formData, setFormData] = useState({
    type: 'incentive',
    amount: '',
    reason: ''
  });
  
  // Incentive Rules & Requests state
  const [activeTab, setActiveTab] = useState('overview');
  const [incentiveRules, setIncentiveRules] = useState([]);
  const [incentiveRequests, setIncentiveRequests] = useState([]);
  const [ruleDialogOpen, setRuleDialogOpen] = useState(false);
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [viewRequestDialog, setViewRequestDialog] = useState(null);
  const [ruleFormData, setRuleFormData] = useState({
    name: '',
    description: '',
    rule_type: 'manual',
    amount: '',
    amount_with_photo: '',
    amount_without_photo: '',
    requires_approval: true,
    requires_screenshot: false,
    is_active: true,
    eligibility: 'all'
  });
  const [requestFormData, setRequestFormData] = useState({
    rule_id: '',
    screenshot_url: '',
    notes: ''
  });
  const [selectedRule, setSelectedRule] = useState(null);
  const [uploadingScreenshot, setUploadingScreenshot] = useState(false);
  const [isRepairingScoping, setIsRepairingScoping] = useState(false);

  // Permission-based access
  const canManageIncentives = hasPermission('incentives.manage');

  // One-click repair for the legacy company-scoping bug — surfaces as
  // a button on the empty Pending Requests view so admins can self-serve
  // the backfill without SSH access. Idempotent on the backend, so it's
  // safe to click multiple times.
  const handleRepairTenantScoping = async () => {
    if (!window.confirm(
      'This will repair recently-orphaned incentive requests by stamping '
      + 'the correct company on each one. Idempotent and safe to run. Continue?'
    )) return;
    setIsRepairingScoping(true);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/incentive-requests/repair-tenant-scoping`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      const { scanned, rewritten, skipped_unresolvable } = res.data || {};
      if (rewritten > 0) {
        toast.success(
          `Repaired ${rewritten} request${rewritten === 1 ? '' : 's'}. `
          + (skipped_unresolvable ? `${skipped_unresolvable} could not be resolved.` : 'Reloading…')
        );
      } else if (scanned === 0) {
        toast.info('Nothing to repair — all requests already carry a company.');
      } else {
        toast.info(`Scanned ${scanned}; nothing in your company needed repair.`);
      }
      await loadIncentiveRequests();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Repair failed.');
    } finally {
      setIsRepairingScoping(false);
    }
  };

  useEffect(() => {
    loadIncentiveRules();
    loadIncentiveRequests();
    if (canManageIncentives) loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManageIncentives]);

  // Pull to refresh handler
  const handlePullRefresh = useCallback(async () => {
    setLoading(true);
    await Promise.all([
      loadIncentives(),
      loadIncentiveRules(),
      loadIncentiveRequests(),
      canManageIncentives ? loadUsers() : Promise.resolve()
    ]);
    toast.success('Refreshed');
  }, [canManageIncentives]);

  const loadIncentives = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const params = { page, page_size: PAGE_SIZE };
      if (filterUserId !== 'all') params.user_id = filterUserId;
      if (filterRange.start) params.start_date = filterRange.start;
      if (filterRange.end) params.end_date = filterRange.end;
      const res = await axios.get(`${API_BASE_URL}/incentives`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setIncentives(res.data.incentives);
      setTotalCount(res.data.total_count || 0);
      setTotals({
        total_incentives: res.data.total_incentives,
        total_penalties: res.data.total_penalties,
        net_total: res.data.net_total
      });
    } catch (error) {
      toast.error('Failed to load incentives');
    } finally {
      setLoading(false);
    }
  }, [page, filterUserId, filterRange]);

  useEffect(() => {
    loadIncentives();
  }, [loadIncentives]);

  const loadAnalytics = useCallback(async () => {
    if (!canManageIncentives) return;
    try {
      const token = localStorage.getItem('token');
      const range = presetRange(analyticsPreset);
      const params = {};
      if (range.start) params.start_date = range.start;
      if (range.end) params.end_date = range.end;
      if (filterUserId !== 'all') params.user_id = filterUserId;
      const res = await axios.get(`${API_BASE_URL}/incentives/analytics`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setAnalytics(res.data);
    } catch (error) {
      console.error('Failed to load analytics');
    }
  }, [canManageIncentives, analyticsPreset, filterUserId]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  const loadUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE_URL}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(res.data);
    } catch (error) {
      console.error('Failed to load users');
    }
  };

  const loadIncentiveRules = async () => {
    try {
      const token = localStorage.getItem('token');
      const endpoint = canManageIncentives ? `${API_BASE_URL}/incentive-rules/all` : `${API_BASE_URL}/incentive-rules`;
      const res = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIncentiveRules(res.data.rules || []);
    } catch (error) {
      console.error('Failed to load incentive rules');
    }
  };

  const loadIncentiveRequests = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE_URL}/incentive-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIncentiveRequests(res.data.requests || []);
    } catch (error) {
      console.error('Failed to load incentive requests');
    }
  };

  const handleCreateRule = async () => {
    if (!ruleFormData.name || !ruleFormData.description) {
      toast.error('Please fill name and description');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/incentive-rules`, ruleFormData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Incentive rule created');
      setRuleDialogOpen(false);
      setRuleFormData({
        name: '', description: '', rule_type: 'manual', amount: '',
        amount_with_photo: '', amount_without_photo: '',
        requires_approval: true, requires_screenshot: false, is_active: true, eligibility: 'all'
      });
      loadIncentiveRules();
    } catch (error) {
      toast.error('Failed to create rule');
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm('Delete this incentive rule?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/incentive-rules/${ruleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Rule deleted');
      loadIncentiveRules();
    } catch (error) {
      toast.error('Failed to delete rule');
    }
  };

  const handleSubmitRequest = async () => {
    if (!requestFormData.rule_id) {
      toast.error('Please select an incentive type');
      return;
    }

    const rule = incentiveRules.find(r => r.id === requestFormData.rule_id);
    if (rule?.requires_screenshot && !requestFormData.screenshot_url) {
      toast.error('Screenshot is required for this incentive');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/incentive-requests`, requestFormData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Request submitted for approval');
      setRequestDialogOpen(false);
      setRequestFormData({ rule_id: '', screenshot_url: '', notes: '' });
      loadIncentiveRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    }
  };

  const handleApproveRequest = async (requestId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE_URL}/incentive-requests/${requestId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Request approved');
      setViewRequestDialog(null);
      loadIncentiveRequests();
      loadIncentives();
    } catch (error) {
      toast.error('Failed to approve');
    }
  };

  const handleRejectRequest = async (requestId, reason) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE_URL}/incentive-requests/${requestId}/reject`, { reason }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Request rejected');
      setViewRequestDialog(null);
      loadIncentiveRequests();
    } catch (error) {
      toast.error('Failed to reject');
    }
  };

  const handleScreenshotUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Simple base64 upload for screenshots
    setUploadingScreenshot(true);
    try {
      const reader = new FileReader();
      reader.onloadend = () => {
        setRequestFormData(prev => ({ ...prev, screenshot_url: reader.result }));
        setUploadingScreenshot(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Failed to upload screenshot');
      setUploadingScreenshot(false);
    }
  };

  const handleCreate = async () => {
    if (!selectedUserId || !formData.amount || !formData.reason) {
      toast.error('Please fill all fields');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/incentives`, {
        user_id: selectedUserId,
        type: formData.type,
        amount: parseFloat(formData.amount),
        reason: formData.reason
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${formData.type === 'incentive' ? 'Incentive' : 'Penalty'} added successfully`);
      setDialogOpen(false);
      setFormData({ type: 'incentive', amount: '', reason: '' });
      setSelectedUserId('');
      loadIncentives();
    } catch (error) {
      toast.error('Failed to create');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/incentives/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Entry deleted');
      loadIncentives();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const renderPagination = () => {
    if (totalCount <= PAGE_SIZE) return null;
    const totalPages = Math.ceil(totalCount / PAGE_SIZE);
    return (
      <div className="flex items-center justify-between mt-4 pt-4 border-t border-wt-border/50">
        <p className="text-sm text-wt-text-muted">
          Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, totalCount)} of {totalCount}
        </p>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            className="border-wt-border"
            data-testid="incentives-prev-page"
          >
            Previous
          </Button>
          <span className="text-sm text-wt-text-secondary px-2">Page {page} / {totalPages}</span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="border-wt-border"
            data-testid="incentives-next-page"
          >
            Next
          </Button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-wt-gold"></div>
      </div>
    );
  }

  return (
    <PullToRefresh onRefresh={handlePullRefresh} disabled={loading}>
    <div className="p-4 sm:p-6 space-y-6 pb-20">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl sm:text-3xl font-bold font-playfair text-wt-text-primary">
          {isAdmin ? 'Incentives, Penalties & Rewards' : 'My Rewards & Incentives'}
        </h1>
      </div>

      <Tabs defaultValue={isAdmin ? "incentives" : "my_incentives"} className="w-full">
        <TabsList className="bg-wt-surface border border-wt-border flex-wrap h-auto">
          {/* My Incentives Tab - For non-admin users */}
          {!isAdmin && (
            <TabsTrigger value="my_incentives" className="data-[state=active]:bg-wt-gold/20 data-[state=active]:text-wt-gold">
              <Wallet className="h-4 w-4 mr-2" />
              My Incentives
            </TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="incentives" className="data-[state=active]:bg-wt-gold/20 data-[state=active]:text-wt-gold">
              <Award className="h-4 w-4 mr-2" />
              Incentives & Penalties
            </TabsTrigger>
          )}
          <TabsTrigger value="rewards" className="data-[state=active]:bg-wt-gold/20 data-[state=active]:text-wt-gold">
            <Target className="h-4 w-4 mr-2" />
            {isAdmin ? 'Reward Rules' : 'Available Rewards'}
          </TabsTrigger>
          <TabsTrigger value="requests" className="data-[state=active]:bg-wt-gold/20 data-[state=active]:text-wt-gold relative">
            <Send className="h-4 w-4 mr-2" />
            {isAdmin ? 'Pending Requests' : 'My Requests'}
            {incentiveRequests.filter(r => r.status === 'pending').length > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                {incentiveRequests.filter(r => r.status === 'pending').length}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        {/* My Incentives Tab - Non-Admin Users */}
        {!isAdmin && (
          <TabsContent value="my_incentives" className="mt-4 space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-wt-paper border border-wt-border rounded-xl p-6"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 rounded-lg bg-green-500/10">
                    <TrendingUp className="h-6 w-6 text-green-400" />
                  </div>
                  <div>
                    <p className="text-wt-text-muted text-sm">Total Earned</p>
                    <p className="text-2xl font-bold text-green-400">{formatCurrency(totals.total_incentives)}</p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-wt-paper border border-wt-border rounded-xl p-6"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 rounded-lg bg-red-500/10">
                    <TrendingDown className="h-6 w-6 text-red-400" />
                  </div>
                  <div>
                    <p className="text-wt-text-muted text-sm">Total Penalties</p>
                    <p className="text-2xl font-bold text-red-400">{formatCurrency(totals.total_penalties)}</p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-wt-paper border border-wt-border rounded-xl p-6"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className={`p-3 rounded-lg ${totals.net_total >= 0 ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                    <Wallet className={`h-6 w-6 ${totals.net_total >= 0 ? 'text-green-400' : 'text-red-400'}`} />
                  </div>
                  <div>
                    <p className="text-wt-text-muted text-sm">Net Balance</p>
                    <p className={`text-2xl font-bold ${totals.net_total >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {totals.net_total >= 0 ? '+' : ''}{formatCurrency(totals.net_total)}
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* My Incentives History */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-wt-paper border border-wt-border rounded-xl p-6"
            >
              <h2 className="text-xl font-semibold text-wt-text-primary mb-4">Transaction History</h2>

              {incentives.length === 0 ? (
                <div className="text-center py-12 text-wt-text-muted">
                  <Award className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No incentives or penalties recorded yet</p>
                  <p className="text-sm mt-2">Complete tasks and claim rewards to earn incentives!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {incentives.map(item => (
                    <div
                      key={item.id}
                      className={`p-4 rounded-lg border ${
                        item.type === 'incentive' 
                          ? 'bg-green-500/5 border-green-500/20' 
                          : 'bg-red-500/5 border-red-500/20'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className={`p-2 rounded-lg ${
                            item.type === 'incentive' ? 'bg-green-500/10' : 'bg-red-500/10'
                          }`}>
                            {item.type === 'incentive' 
                              ? <Award className="h-5 w-5 text-green-400" />
                              : <AlertCircle className="h-5 w-5 text-red-400" />
                            }
                          </div>
                          <div>
                            <p className={`font-semibold ${
                              item.type === 'incentive' ? 'text-green-400' : 'text-red-400'
                            }`}>
                              {item.type === 'incentive' ? '+' : '-'}{formatCurrency(item.amount)}
                            </p>
                            <p className="text-sm text-wt-text-secondary mt-1">{item.reason}</p>
                            {/* Show order details if it's an order incentive */}
                            {item.details?.order_number && (
                              <p className="text-xs text-wt-gold mt-1">
                                Order: {item.details.order_number}
                              </p>
                            )}
                            <p className="text-xs text-wt-text-muted flex items-center gap-1 mt-2">
                              <Calendar className="h-3 w-3" />
                              {new Date(item.created_at).toLocaleDateString()} at {new Date(item.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                            </p>
                          </div>
                        </div>
                        <div className={`px-2 py-1 rounded text-xs ${
                          item.type === 'incentive' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                        }`}>
                          {item.type === 'incentive' ? 'Incentive' : 'Penalty'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {renderPagination()}
            </motion.div>
          </TabsContent>
        )}

        {/* Incentives Tab - Admin Only */}
        {isAdmin && (
        <TabsContent value="incentives" className="mt-4 space-y-6">
          {canManageIncentives && (
            <div className="flex justify-end">
              <Button onClick={() => setDialogOpen(true)} className="bg-wt-gold hover:bg-wt-gold-hover text-black">
                <Plus className="h-4 w-4 mr-2" />
                Add Incentive/Penalty
              </Button>
            </div>
          )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-wt-paper border border-wt-border rounded-xl p-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 rounded-lg bg-green-500/10">
              <TrendingUp className="h-6 w-6 text-green-400" />
            </div>
            <div>
              <p className="text-wt-text-muted text-sm">Total Incentives</p>
              <p className="text-2xl font-bold text-green-400">{formatCurrency(totals.total_incentives)}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-wt-paper border border-wt-border rounded-xl p-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 rounded-lg bg-red-500/10">
              <TrendingDown className="h-6 w-6 text-red-400" />
            </div>
            <div>
              <p className="text-wt-text-muted text-sm">Total Penalties</p>
              <p className="text-2xl font-bold text-red-400">{formatCurrency(totals.total_penalties)}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-wt-paper border border-wt-border rounded-xl p-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 rounded-lg bg-wt-gold/10">
              <DollarSign className="h-6 w-6 text-wt-gold" />
            </div>
            <div>
              <p className="text-wt-text-muted text-sm">Net Total</p>
              <p className={`text-2xl font-bold ${totals.net_total >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {totals.net_total >= 0 ? '+' : ''}{formatCurrency(totals.net_total)}
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Analytics */}
      {canManageIncentives && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="bg-wt-paper border border-wt-border rounded-xl p-6"
          data-testid="incentives-analytics"
        >
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h2 className="text-xl font-semibold text-wt-text-primary">Incentive Analytics</h2>
            <Select value={analyticsPreset} onValueChange={setAnalyticsPreset}>
              <SelectTrigger className="w-[180px] bg-wt-bg border-wt-border text-wt-text-primary" data-testid="analytics-range-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-wt-paper border-wt-border">
                <SelectItem value="mtd">Month to date</SelectItem>
                <SelectItem value="last_30">Last 30 days</SelectItem>
                <SelectItem value="last_month">Last month</SelectItem>
                <SelectItem value="last_90">Last 90 days</SelectItem>
                <SelectItem value="ytd">Year to date</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {!analytics || analytics.by_employee.length === 0 ? (
            <p className="text-center text-wt-text-muted py-8">No incentive data for this period</p>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-wt-text-muted mb-2">By employee</p>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={analytics.by_employee.slice(0, 12)} margin={{ bottom: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                    <XAxis dataKey="user_name" angle={-35} textAnchor="end" interval={0} tick={{ fill: '#9ca3af', fontSize: 11 }} />
                    <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} />
                    <Tooltip contentStyle={{ background: '#1a1a1a', border: '1px solid #333', borderRadius: 8 }} labelStyle={{ color: '#e5e5e5' }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="incentives" name="Incentives" fill="#22c55e" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="penalties" name="Penalties" fill="#ef4444" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div>
                <p className="text-sm text-wt-text-muted mb-2">Daily trend</p>
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={analytics.series} margin={{ bottom: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                    <XAxis dataKey="date" angle={-35} textAnchor="end" tick={{ fill: '#9ca3af', fontSize: 11 }} />
                    <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} />
                    <Tooltip contentStyle={{ background: '#1a1a1a', border: '1px solid #333', borderRadius: 8 }} labelStyle={{ color: '#e5e5e5' }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Line type="monotone" dataKey="incentives" name="Incentives" stroke="#22c55e" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="penalties" name="Penalties" stroke="#ef4444" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </motion.div>
      )}

      {/* Incentives List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-wt-paper border border-wt-border rounded-xl p-6"
      >
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h2 className="text-xl font-semibold text-wt-text-primary">History</h2>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={filterUserId} onValueChange={(v) => { setFilterUserId(v); setPage(1); }}>
              <SelectTrigger className="w-[190px] bg-wt-bg border-wt-border text-wt-text-primary" data-testid="history-employee-filter">
                <SelectValue placeholder="All employees" />
              </SelectTrigger>
              <SelectContent className="bg-wt-paper border-wt-border max-h-[280px]">
                <SelectItem value="all">All employees</SelectItem>
                {users.map(u => (
                  <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <DateInput
              value={filterRange.start}
              onChange={(e) => { setFilterRange(r => ({ ...r, start: e.target.value })); setPage(1); }}
              max={filterRange.end || undefined}
              className="w-[150px] bg-wt-bg border-wt-border text-wt-text-primary"
              data-testid="history-start-date"
            />
            <span className="text-wt-text-muted text-sm">to</span>
            <DateInput
              value={filterRange.end}
              onChange={(e) => { setFilterRange(r => ({ ...r, end: e.target.value })); setPage(1); }}
              min={filterRange.start || undefined}
              className="w-[150px] bg-wt-bg border-wt-border text-wt-text-primary"
              data-testid="history-end-date"
            />
            {(filterUserId !== 'all' || filterRange.start || filterRange.end) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setFilterUserId('all'); setFilterRange({ start: '', end: '' }); setPage(1); }}
                className="text-wt-text-muted hover:text-wt-gold"
                data-testid="history-clear-filters"
              >
                Clear
              </Button>
            )}
          </div>
        </div>

        {incentives.length === 0 ? (
          <div className="text-center py-12 text-wt-text-muted">
            <Award className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>No incentives or penalties recorded yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {incentives.map(item => (
              <div
                key={item.id}
                className={`p-4 rounded-lg border ${
                  item.type === 'incentive' 
                    ? 'bg-green-500/5 border-green-500/20' 
                    : 'bg-red-500/5 border-red-500/20'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${
                      item.type === 'incentive' ? 'bg-green-500/10' : 'bg-red-500/10'
                    }`}>
                      {item.type === 'incentive' 
                        ? <Award className="h-5 w-5 text-green-400" />
                        : <AlertCircle className="h-5 w-5 text-red-400" />
                      }
                    </div>
                    <div>
                      <p className={`font-semibold ${
                        item.type === 'incentive' ? 'text-green-400' : 'text-red-400'
                      }`}>
                        {item.type === 'incentive' ? '+' : '-'}{formatCurrency(item.amount)}
                      </p>
                      {canManageIncentives && (
                        <p className="text-sm text-wt-text-muted flex items-center gap-1">
                          <User className="h-3 w-3" /> {item.user_name}
                        </p>
                      )}
                      <p className="text-sm text-wt-text-secondary mt-1">{item.reason}</p>
                      <p className="text-xs text-wt-text-muted flex items-center gap-1 mt-2">
                        <Calendar className="h-3 w-3" />
                        {new Date(item.created_at).toLocaleDateString()} - by {item.created_by_name}
                      </p>
                    </div>
                  </div>
                  {canManageIncentives && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(item.id)}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        {renderPagination()}
      </motion.div>
        </TabsContent>
        )}

        {/* Rewards/Rules Tab */}
        <TabsContent value="rewards" className="mt-4 space-y-6">
          {canManageIncentives && (
            <div className="flex justify-end">
              <Button onClick={() => setRuleDialogOpen(true)} className="bg-wt-gold hover:bg-wt-gold-hover text-black">
                <Plus className="h-4 w-4 mr-2" />
                Create Reward Rule
              </Button>
            </div>
          )}

          {/* Available Rewards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {incentiveRules.map((rule) => (
              <motion.div
                key={rule.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className={`bg-wt-paper border rounded-xl p-6 ${rule.is_active ? 'border-wt-border' : 'border-red-500/30 opacity-60'}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="p-2 rounded-lg bg-wt-gold/10">
                    {rule.rule_type === 'review' ? <Star className="h-5 w-5 text-wt-gold" /> :
                     rule.rule_type === 'performance' ? <Award className="h-5 w-5 text-wt-gold" /> :
                     <Target className="h-5 w-5 text-wt-gold" />}
                  </div>
                  {canManageIncentives && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeleteRule(rule.id)}
                      className="text-red-400 hover:text-red-300 h-8 w-8 p-0"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                <h3 className="font-semibold text-wt-text-primary mb-1">{rule.name}</h3>
                <p className="text-sm text-wt-text-muted mb-3 line-clamp-2">{rule.description}</p>
                
                <div className="space-y-2 mb-4">
                  {rule.rule_type === 'review' ? (
                    <>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-wt-text-secondary">With Photo</span>
                        <span className="font-semibold text-green-400">{formatCurrency(rule.amount_with_photo)}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-wt-text-secondary">Without Photo</span>
                        <span className="font-semibold text-yellow-400">{formatCurrency(rule.amount_without_photo)}</span>
                      </div>
                    </>
                  ) : (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-wt-text-secondary">Reward Amount</span>
                      <span className="font-semibold text-green-400">{formatCurrency(rule.amount)}</span>
                    </div>
                  )}
                  
                  <div className="flex gap-2 pt-2">
                    {rule.requires_screenshot && (
                      <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded">Screenshot Required</span>
                    )}
                    {rule.requires_approval && (
                      <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-1 rounded">Needs Approval</span>
                    )}
                  </div>
                </div>
                
                {!canManageIncentives && (
                  <Button 
                    onClick={() => {
                      setRequestFormData(prev => ({ ...prev, rule_id: rule.id }));
                      setSelectedRule(rule);
                      setRequestDialogOpen(true);
                    }}
                    className="w-full bg-wt-gold hover:bg-wt-gold-hover text-black"
                    size="sm"
                  >
                    <Send className="h-4 w-4 mr-2" />
                    Claim Reward
                  </Button>
                )}
              </motion.div>
            ))}
          </div>

          {incentiveRules.length === 0 && (
            <div className="text-center py-12">
              <Target className="h-12 w-12 mx-auto text-wt-text-muted mb-4" />
              <p className="text-wt-text-muted">No reward rules available</p>
              {canManageIncentives && (
                <p className="text-wt-text-secondary text-sm mt-2">Create reward rules to motivate your team</p>
              )}
            </div>
          )}
        </TabsContent>

        {/* Requests Tab */}
        <TabsContent value="requests" className="mt-4 space-y-6">
          {!canManageIncentives && (
            <div className="flex justify-end">
              <Button 
                onClick={() => setRequestDialogOpen(true)} 
                className="bg-wt-gold hover:bg-wt-gold-hover text-black"
                disabled={incentiveRules.length === 0}
              >
                <Plus className="h-4 w-4 mr-2" />
                New Request
              </Button>
            </div>
          )}

          {/* Pending Requests for Admin */}
          {canManageIncentives && incentiveRequests.filter(r => r.status === 'pending').length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-wt-text-primary mb-4">Pending Approval</h3>
              <div className="space-y-3">
                {incentiveRequests.filter(r => r.status === 'pending').map((request) => (
                  <motion.div
                    key={request.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="bg-wt-paper border border-yellow-500/30 rounded-xl p-4"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-wt-gold/20 flex items-center justify-center">
                          <User className="h-5 w-5 text-wt-gold" />
                        </div>
                        <div>
                          <p className="font-semibold text-wt-text-primary">{request.user_name}</p>
                          <p className="text-sm text-wt-text-secondary">
                            {request.request_type === 'order_incentive' 
                              ? `Order Incentive - #${request.order_number}` 
                              : request.rule_name}
                          </p>
                          <p className="text-xs text-wt-text-muted">
                            {new Date(request.created_at).toLocaleDateString()} • {formatCurrency(request.amount)}
                            {request.request_type === 'order_incentive' && request.order_value && (
                              <span className="ml-2">({request.percentage}% of {formatCurrency(request.order_value)})</span>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {request.has_screenshot && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setViewRequestDialog(request)}
                            className="border-wt-border"
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View
                          </Button>
                        )}
                        <Button
                          size="sm"
                          onClick={() => handleApproveRequest(request.id)}
                          className="bg-green-500 hover:bg-green-600 text-white"
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleRejectRequest(request.id, 'Rejected by admin')}
                          className="bg-red-500 hover:bg-red-600 text-white"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {/* All Requests List */}
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-wt-text-primary">
              {canManageIncentives ? 'All Requests' : 'My Requests'}
            </h3>
            {incentiveRequests.length === 0 ? (
              <div className="text-center py-12 bg-wt-paper border border-wt-border rounded-xl" data-testid="incentives-empty-state">
                <Send className="h-12 w-12 mx-auto text-wt-text-muted mb-4" />
                <p className="text-wt-text-muted">No requests yet</p>
                {canManageIncentives && (
                  <div className="mt-6 max-w-md mx-auto">
                    <p className="text-xs text-wt-text-muted leading-relaxed">
                      Were you expecting to see pending requests here?
                      Recent requests created during a brief tenant-scoping bug
                      may be missing their company tag — click below to repair them.
                    </p>
                    <Button
                      onClick={handleRepairTenantScoping}
                      disabled={isRepairingScoping}
                      variant="outline"
                      size="sm"
                      className="mt-3 border-wt-border text-wt-text-primary"
                      data-testid="repair-incentive-requests-btn"
                    >
                      {isRepairingScoping ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Wrench className="h-4 w-4 mr-2" />
                      )}
                      Repair pending requests
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                {incentiveRequests.map((request) => (
                  <div
                    key={request.id}
                    className={`bg-wt-paper border rounded-lg p-4 ${
                      request.status === 'pending' ? 'border-yellow-500/30' :
                      request.status === 'approved' ? 'border-green-500/30' : 'border-red-500/30'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-wt-text-primary">
                          {request.request_type === 'order_incentive' 
                            ? `Order Incentive - #${request.order_number}` 
                            : request.rule_name}
                        </p>
                        {canManageIncentives && <p className="text-sm text-wt-text-secondary">{request.user_name}</p>}
                        <p className="text-xs text-wt-text-muted">
                          {new Date(request.created_at).toLocaleDateString()}
                          {request.request_type === 'order_incentive' && request.order_value && (
                            <span className="ml-2">• {request.percentage}% of {formatCurrency(request.order_value)}</span>
                          )}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-wt-gold">{formatCurrency(request.amount)}</p>
                        <span className={`text-xs px-2 py-1 rounded ${
                          request.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                          request.status === 'approved' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                        }`}>
                          {request.status}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Add Incentive/Penalty Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-wt-paper border-wt-border">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary font-playfair text-2xl">
              Add Incentive/Penalty
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-wt-text-secondary">User</Label>
              <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary mt-1">
                  <SelectValue placeholder="Select user" />
                </SelectTrigger>
                <SelectContent className="bg-wt-paper border-wt-border">
                  {users.map(u => (
                    <SelectItem key={u.id} value={u.id} className="text-wt-text-primary">
                      {u.full_name} ({u.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-wt-text-secondary">Type</Label>
              <Select value={formData.type} onValueChange={v => setFormData({...formData, type: v})}>
                <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-wt-paper border-wt-border">
                  <SelectItem value="incentive" className="text-green-400">Incentive (+)</SelectItem>
                  <SelectItem value="penalty" className="text-red-400">Penalty (-)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-wt-text-secondary">Amount (AED)</Label>
              <Input
                type="number"
                value={formData.amount}
                onChange={e => setFormData({...formData, amount: e.target.value})}
                className="bg-wt-bg border-wt-border text-wt-text-primary mt-1"
                placeholder="Enter amount"
              />
            </div>

            <div>
              <Label className="text-wt-text-secondary">Reason</Label>
              <Textarea
                value={formData.reason}
                onChange={e => setFormData({...formData, reason: e.target.value})}
                className="bg-wt-bg border-wt-border text-wt-text-primary mt-1"
                placeholder="Enter reason for this incentive/penalty"
                rows={3}
              />
            </div>

            <div className="flex justify-end gap-3">
              <Button variant="ghost" onClick={() => setDialogOpen(false)} className="text-wt-text-secondary">
                Cancel
              </Button>
              <Button onClick={handleCreate} className="bg-wt-gold hover:bg-wt-gold-hover text-black">
                Add Entry
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Rule Dialog */}
      <Dialog open={ruleDialogOpen} onOpenChange={setRuleDialogOpen}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary font-playfair text-2xl">
              Create Reward Rule
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-wt-text-secondary">Rule Name *</Label>
              <Input
                value={ruleFormData.name}
                onChange={e => setRuleFormData({...ruleFormData, name: e.target.value})}
                className="bg-wt-bg border-wt-border text-wt-text-primary mt-1"
                placeholder="e.g., Google Review Incentive"
              />
            </div>

            <div>
              <Label className="text-wt-text-secondary">Description *</Label>
              <Textarea
                value={ruleFormData.description}
                onChange={e => setRuleFormData({...ruleFormData, description: e.target.value})}
                className="bg-wt-bg border-wt-border text-wt-text-primary mt-1"
                placeholder="How to earn this reward..."
                rows={2}
              />
            </div>

            <div>
              <Label className="text-wt-text-secondary">Rule Type</Label>
              <Select
                value={ruleFormData.rule_type}
                onValueChange={v => setRuleFormData({...ruleFormData, rule_type: v})}
              >
                <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-wt-paper border-wt-border">
                  <SelectItem value="manual">Manual (General)</SelectItem>
                  <SelectItem value="review">Google Review</SelectItem>
                  <SelectItem value="performance">Best Performance</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {ruleFormData.rule_type === 'review' ? (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-wt-text-secondary">Amount with Photo (AED)</Label>
                  <Input
                    type="number"
                    value={ruleFormData.amount_with_photo}
                    onChange={e => setRuleFormData({...ruleFormData, amount_with_photo: e.target.value})}
                    className="bg-wt-bg border-wt-border text-wt-text-primary mt-1"
                    placeholder="10"
                  />
                </div>
                <div>
                  <Label className="text-wt-text-secondary">Amount without Photo (AED)</Label>
                  <Input
                    type="number"
                    value={ruleFormData.amount_without_photo}
                    onChange={e => setRuleFormData({...ruleFormData, amount_without_photo: e.target.value})}
                    className="bg-wt-bg border-wt-border text-wt-text-primary mt-1"
                    placeholder="5"
                  />
                </div>
              </div>
            ) : (
              <div>
                <Label className="text-wt-text-secondary">Amount (AED)</Label>
                <Input
                  type="number"
                  value={ruleFormData.amount}
                  onChange={e => setRuleFormData({...ruleFormData, amount: e.target.value})}
                  className="bg-wt-bg border-wt-border text-wt-text-primary mt-1"
                  placeholder="Enter reward amount"
                />
              </div>
            )}

            <div className="flex items-center justify-between">
              <Label className="text-wt-text-secondary">Requires Screenshot</Label>
              <Switch
                checked={ruleFormData.requires_screenshot}
                onCheckedChange={v => setRuleFormData({...ruleFormData, requires_screenshot: v})}
              />
            </div>

            <div className="flex items-center justify-between">
              <Label className="text-wt-text-secondary">Requires Approval</Label>
              <Switch
                checked={ruleFormData.requires_approval}
                onCheckedChange={v => setRuleFormData({...ruleFormData, requires_approval: v})}
              />
            </div>

            <div className="flex items-center justify-between">
              <Label className="text-wt-text-secondary">Active</Label>
              <Switch
                checked={ruleFormData.is_active}
                onCheckedChange={v => setRuleFormData({...ruleFormData, is_active: v})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRuleDialogOpen(false)} className="text-wt-text-secondary">
              Cancel
            </Button>
            <Button onClick={handleCreateRule} className="bg-wt-gold hover:bg-wt-gold-hover text-black">
              Create Rule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Submit Request Dialog */}
      <Dialog open={requestDialogOpen} onOpenChange={setRequestDialogOpen}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary font-playfair text-2xl">
              Claim Reward
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-wt-text-secondary">Select Reward Type *</Label>
              <Select
                value={requestFormData.rule_id}
                onValueChange={v => {
                  setRequestFormData({...requestFormData, rule_id: v});
                  setSelectedRule(incentiveRules.find(r => r.id === v));
                }}
              >
                <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary mt-1">
                  <SelectValue placeholder="Select a reward..." />
                </SelectTrigger>
                <SelectContent className="bg-wt-paper border-wt-border">
                  {incentiveRules.map(rule => (
                    <SelectItem key={rule.id} value={rule.id}>{rule.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedRule && (
              <div className="p-3 bg-wt-bg rounded-lg">
                <p className="text-sm text-wt-text-secondary">{selectedRule.description}</p>
                {selectedRule.rule_type === 'review' ? (
                  <p className="text-sm text-wt-gold mt-2">
                    Earn {formatCurrency(selectedRule.amount_with_photo)} with photo or {formatCurrency(selectedRule.amount_without_photo)} without
                  </p>
                ) : (
                  <p className="text-sm text-wt-gold mt-2">Reward: {formatCurrency(selectedRule.amount)}</p>
                )}
              </div>
            )}

            {selectedRule?.requires_screenshot && (
              <div>
                <Label className="text-wt-text-secondary">Screenshot *</Label>
                <div className="mt-2">
                  {requestFormData.screenshot_url ? (
                    <div className="relative">
                      <img 
                        src={requestFormData.screenshot_url} 
                        alt="Screenshot" 
                        className="w-full max-h-48 object-contain rounded-lg border border-wt-border"
                      />
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setRequestFormData({...requestFormData, screenshot_url: ''})}
                        className="absolute top-2 right-2 bg-red-500 text-white hover:bg-red-600"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-wt-border rounded-lg cursor-pointer hover:border-wt-gold transition-colors">
                      <Image className="h-8 w-8 text-wt-text-muted mb-2" />
                      <span className="text-sm text-wt-text-muted">Click to upload screenshot</span>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleScreenshotUpload}
                        disabled={uploadingScreenshot}
                      />
                    </label>
                  )}
                </div>
              </div>
            )}

            {selectedRule?.rule_type === 'review' && !selectedRule?.requires_screenshot && (
              <div>
                <Label className="text-wt-text-secondary">Screenshot (Optional - for higher reward)</Label>
                <div className="mt-2">
                  {requestFormData.screenshot_url ? (
                    <div className="relative">
                      <img 
                        src={requestFormData.screenshot_url} 
                        alt="Screenshot" 
                        className="w-full max-h-48 object-contain rounded-lg border border-wt-border"
                      />
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setRequestFormData({...requestFormData, screenshot_url: ''})}
                        className="absolute top-2 right-2 bg-red-500 text-white hover:bg-red-600"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-wt-border rounded-lg cursor-pointer hover:border-wt-gold transition-colors">
                      <Camera className="h-6 w-6 text-wt-text-muted mb-1" />
                      <span className="text-xs text-wt-text-muted">Add photo for {formatCurrency(selectedRule.amount_with_photo)}</span>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleScreenshotUpload}
                        disabled={uploadingScreenshot}
                      />
                    </label>
                  )}
                </div>
              </div>
            )}

            <div>
              <Label className="text-wt-text-secondary">Notes (Optional)</Label>
              <Textarea
                value={requestFormData.notes}
                onChange={e => setRequestFormData({...requestFormData, notes: e.target.value})}
                className="bg-wt-bg border-wt-border text-wt-text-primary mt-1"
                placeholder="Any additional details..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => {
              setRequestDialogOpen(false);
              setRequestFormData({ rule_id: '', screenshot_url: '', notes: '' });
              setSelectedRule(null);
            }} className="text-wt-text-secondary">
              Cancel
            </Button>
            <Button onClick={handleSubmitRequest} className="bg-wt-gold hover:bg-wt-gold-hover text-black">
              Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Request Dialog (Admin) */}
      <Dialog open={!!viewRequestDialog} onOpenChange={() => setViewRequestDialog(null)}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary font-playfair text-2xl">
              Review Request
            </DialogTitle>
          </DialogHeader>
          {viewRequestDialog && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-wt-gold/20 flex items-center justify-center">
                  <User className="h-6 w-6 text-wt-gold" />
                </div>
                <div>
                  <p className="font-semibold text-wt-text-primary">{viewRequestDialog.user_name}</p>
                  <p className="text-sm text-wt-text-secondary">{viewRequestDialog.rule_name}</p>
                </div>
              </div>
              
              <div className="p-3 bg-wt-bg rounded-lg">
                <p className="text-sm text-wt-text-muted mb-1">Reward Amount</p>
                <p className="text-xl font-bold text-wt-gold">{formatCurrency(viewRequestDialog.amount)}</p>
              </div>

              {viewRequestDialog.screenshot_url && (
                <div>
                  <p className="text-sm text-wt-text-muted mb-2">Screenshot</p>
                  <img 
                    src={viewRequestDialog.screenshot_url} 
                    alt="Screenshot" 
                    className="w-full max-h-64 object-contain rounded-lg border border-wt-border"
                  />
                </div>
              )}

              {viewRequestDialog.notes && (
                <div>
                  <p className="text-sm text-wt-text-muted mb-1">Notes</p>
                  <p className="text-wt-text-secondary">{viewRequestDialog.notes}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button 
              variant="ghost" 
              onClick={() => setViewRequestDialog(null)} 
              className="text-wt-text-secondary"
            >
              Close
            </Button>
            {viewRequestDialog?.status === 'pending' && (
              <>
                <Button 
                  onClick={() => handleRejectRequest(viewRequestDialog.id, 'Rejected')}
                  className="bg-red-500 hover:bg-red-600 text-white"
                >
                  <X className="h-4 w-4 mr-2" />
                  Reject
                </Button>
                <Button 
                  onClick={() => handleApproveRequest(viewRequestDialog.id)}
                  className="bg-green-500 hover:bg-green-600 text-white"
                >
                  <Check className="h-4 w-4 mr-2" />
                  Approve
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </PullToRefresh>
  );
};

export default IncentivesPage;
